// Review sorting and helper functions

/**
 * Sort reviews by given criteria
 * @param {Array} reviews - Array of review objects
 * @param {string} sortBy - Sort criteria (newest|oldest|highest|lowest|helpful)
 * @returns {Array} Sorted reviews array
 */
export const sortReviews = (reviews, sortBy) => {
  return [...reviews].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt);
      case 'oldest':
        return new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt);
      case 'highest':
        return b.rating - a.rating;
      case 'lowest':
        return a.rating - b.rating;
      case 'helpful':
        return b.helpful - a.helpful;
      default:
        return 0;
    }
  });
};

/**
 * Calculate average rating from reviews array
 * @param {Array} reviews - Array of review objects
 * @param {number} fallback - Fallback rating if no reviews
 * @returns {string} Average rating (1 decimal place)
 */
export const calculateAvgRating = (reviews, fallback = 0) => {
  if (reviews.length === 0) return fallback;
  return (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1);
};

/**
 * Calculate rating distribution (5-star breakdown)
 * @param {Array} reviews - Array of review objects
 * @returns {Array} Array of {star, count, percent} objects
 */
export const getRatingDistribution = (reviews) => {
  return [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
    percent:
      reviews.length > 0
        ? Math.round((reviews.filter((r) => r.rating === star).length / reviews.length) * 100)
        : 0,
  }));
};

/**
 * Create a new review object
 * @param {string} userName - Name of the reviewer
 * @param {number} rating - Star rating (1-5)
 * @param {string} text - Review text
 * @returns {object} New review object
 */
export const createReview = (userName, rating, text) => {
  return {
    id: Date.now(),
    user: userName || 'Anonymous',
    rating,
    date: new Date().toISOString().split('T')[0],
    text: text.trim(),
    helpful: 0,
  };
};
